'use client';
import { cx } from '@linaria/core';
import { useSearchParams } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';

import {
	RegisterInbound,
	RegisterOutbound,
} from '~features/annals/enums/register';
import annalsBotWizard from '~features/annals/hooks/botWizard';
import { useRequireAuth } from '~features/auth';
import { IdHideEmailConfirmModal } from '~features/emailConfirm';
import { useEmailConfirm } from '~features/emailConfirm/hooks/useEmailConfirm';
import { Toaster } from '~features/toaster';
import { useModal } from '~hooks/useModal';
import { useSession } from '~hooks/useSession';
import { useTranslations } from '~i18n/hooks';
import { usePathname, useRouter } from '~i18n/navigation';
import ArrowBackIcon from '~public/assets/icons/arrowBack.svg';
import type { GenerationApiErrorCodesKeys } from '~services/api/generation';
import {
	generationApi,
	generationApiErrorCodes,
} from '~services/api/generation';
import { userApi } from '~services/api/user';
import { isApiErrorWithCode } from '~shared/api/origin/utils';
import type {
	BotWizardLoaderText,
	BotWizardQuote,
	Translated,
} from '~shared/api/sanity';
import { fadeIn } from '~theme/snippets';
import { updateQueryParamsWithPathname } from '~utils/searchParams';

import { useQueryData } from '../../hooks/useQueryData';
import { useUpdateQuery } from '../../hooks/useUpdateQuery';
import { CUSTOM_VALUE_QUERY_PARAM_VALUE } from '../../model/constants';
import { isValueMultiSelect, isValueSimple } from '../../model/guards';
import type {
	BotWizardFieldDataSlider,
	BotWizardFieldNames,
	BotWizardValueSimple,
	BotWizardValueSliders,
	TBotWizard,
} from '../../model/types';
import { getDependencyContent, getDependencyStep } from '../../model/utils';
import { ChooseSteps } from '../ChooseSteps';
import { ErrorModal } from '../ErrorModal';
import { Field } from '../Field';
import { LoadingModal } from '../LoadingModal';
import { NextButton } from '../NextButton';
import { PreviewStep } from '../PreviewStep';
import { ProgressItem } from '../ProgressItem';
import { PromptStep } from '../PromptStep';
import { ResultModal } from '../ResultModal';
import {
	BackButton,
	Description,
	HeaderStepsWrapper,
	HeaderWrapper,
	StepWrapper,
	StyledBotWizard,
	StyledTitle,
} from './styles';

export const BotWizard = ({
	fields,
	presetSteps,
	firstStep,
	previewPresetStep,
}: TBotWizard) => {
	const [personality, setPersonality] = useState<string>('');
	const [look, setLook] = useState<string>('');
	const [showEmailVer, setShowEmailVer] = useState<boolean>(false);
	const [personalityError, setPersonalityError] = useState<string>('');
	const [errorCode, setErrorCode] = useState<string>(
		generationApiErrorCodes.unknown
	);
	const [nameError, setNameError] = useState<string>('');
	const { userId, isSuccess, isDemoUser } = useSession();
	const router = useRouter();
	const sendBotOpenedAnnals = annalsBotWizard.useGenerateBotOpened();
	const pathname = usePathname();
	const searchParams = useSearchParams();
	const autocompleteRef = useRef(searchParams.get('autocomplete') === 'true');
	const { values, customValues } = useQueryData({
		fields,
		presetSteps,
		firstStep,
	});

	const t = useTranslations('BotWizard');

	useEffect(() => {
		setNameError('');
	}, [values.name]);
	const { required, confirmed, forceHidden } = useEmailConfirm();

	const withRequireAuth = useRequireAuth({ preventAction: !forceHidden });

	const sendBotGenerationErrorAnnals = annalsBotWizard.useGenerateBotError();
	const [
		triggerGeneratePrompt,
		{ data: generatedPrompt, isLoading: isGeneratedPromptLoading },
	] = generationApi.useGeneratePromptMutation();

	const [
		triggerGenerateBotPrompt,
		{
			data: generatedBotPrompt,
			isLoading: generationBotPromptLoading,
			reset: resetGeneratedBotPrompt,
		},
	] = generationApi.useGenerateBotPromptMutation();

	const [
		triggerGenerateBotPreset,
		{
			data: generatedBotPreset,
			isLoading: generationBotPresetLoading,
			reset: resetGeneratedBotPreset,
		},
	] = generationApi.useGenerateBotPresetMutation();

	const {
		isOpen: isErrorModalOpen,
		open: errorModalOpen,
		close: errorModalClose,
	} = useModal();

	const generateBotDescription = annalsBotWizard.useGenerateBotDescription();
	const generateBotSummary = annalsBotWizard.useGenerateBotSummary();

	const getStep = () => {
		return getDependencyStep({
			step: presetSteps[+values.step! - 1].data,
			values,
			contentDependency: ['gender'],
		});
	};

	const getSimpleValue = (key: BotWizardFieldNames): string | undefined => {
		if (isValueSimple(fields[key], values, key)) {
			if (values[key] === CUSTOM_VALUE_QUERY_PARAM_VALUE) {
				return customValues[key];
			} else {
				return values[key];
			}
		}
		return undefined;
	};
	const [trigger] = userApi.useUpdateRegistrationPropsMutation();

	const generateUsingPrompt = async () => {
		if (!getSimpleValue('name') || !personality) {
			if (!getSimpleValue('name')) {
				setNameError(t('requiredFieldErrorMessage'));
			}
			if (!personality) {
				setPersonalityError(t('requiredFieldErrorMessage'));
			}
		} else {
			generateBotDescription({
				prompt: personality,
				prompt_look: look,
				name: values.name as string,
			});
			await triggerGenerateBotPrompt({
				userId,
				name: values.name as string,
				gender: values.gender as string,
				style: values.style as string,
				appearance: look,
				description: personality,
			}).unwrap();
		}
	};

	const generateUsingPreset = async () => {
		const getPersonality = () => {
			const fieldData = fields.personality.data as BotWizardFieldDataSlider;
			const localData = getDependencyContent({
				data: fieldData.presets,
				contentDependency: fields.personality.contentDependency,
				values,
			});
			const personality = values.personality as BotWizardValueSliders;

			if (personality?.preset === 'custom') {
				return Object.fromEntries(
					Object.entries(personality.values).map(([key, value]) => [
						key,
						parseInt(value),
					])
				);
			} else {
				const preset = localData?.find((el) => el.id === personality?.preset);
				return Object.fromEntries(
					fieldData.sliders.map((el, index) => [
						el.id,
						preset?.values[index] || 0,
					])
				);
			}
		};

		const getMultiselectValue = (key: BotWizardFieldNames): string[] => {
			if (isValueMultiSelect(fields[key], values, key)) {
				return values[key] || [];
			} else {
				return [];
			}
		};
		generateBotSummary();
		await triggerGenerateBotPreset({
			userId,
			name: getSimpleValue('name') || '',
			gender: getSimpleValue('gender') || '',
			style: getSimpleValue('style') || '',
			age: parseInt(getSimpleValue('age') || '18'),
			height: getSimpleValue('height') || '',
			ethnic: getSimpleValue('ethnic') || '',
			physique: getSimpleValue('physique') || '',
			boobs:
				values.gender === 'male' ? undefined : getSimpleValue('boobs') || '',
			butt: values.gender === 'male' ? undefined : getSimpleValue('butt') || '',
			penisSize:
				values.gender === 'female'
					? undefined
					: getSimpleValue('penisSize') || '',
			hairStyle: getSimpleValue('hairStyle') || '',
			hairColor: getSimpleValue('hairColor') || '',
			eyeColor: getSimpleValue('eyeColor') || '',
			orientation: getSimpleValue('orientation') || '',
			chatStyle: getSimpleValue('chatStyle') || '',
			occupation: getSimpleValue('occupation'),
			startingRelationship: getSimpleValue('startingRelationship'),
			kinks: getMultiselectValue('kinks') || [],
			personality: getPersonality(),
		}).unwrap();
	};

	// eslint-disable-next-line react-hooks/exhaustive-deps
	const onGenerate = (usingPrompt = false) => {
		const onError = (err: unknown) => {
			const code =
				(isApiErrorWithCode(err) &&
					generationApiErrorCodes[
						err.data.error as GenerationApiErrorCodesKeys
					]) ||
				generationApiErrorCodes.unknown;
			setErrorCode(code);
			errorModalOpen()();
			void sendBotGenerationErrorAnnals({
				type_error:
					code === generationApiErrorCodes.unsafeAvatar
						? 'safety_error'
						: 'generation_error',
			});
		};

		setShowEmailVer(true);
		if (required) return;
		withRequireAuth(
			() => {
				autocompleteRef.current = false;
				router.push(
					updateQueryParamsWithPathname(
						{ autocomplete: undefined },
						pathname,
						searchParams
					)
				);
				const promise = usingPrompt ? generateUsingPrompt : generateUsingPreset;
				promise().catch((e) => {
					onError(e);
				});
			},
			{
				analytics: {
					inbound: RegisterInbound.GenerateCharacter,
					outbound: RegisterOutbound.GeneratorBot,
				},
			}
		)();
	};

	useEffect(() => {
		if (generatedPrompt) {
			setLook(generatedPrompt.appearance);
			setPersonality(generatedPrompt.description);
		}
	}, [generatedPrompt]);

	const isFinalPromptStep = values.path === '1' && values.step === '1';

	const isFinalPresetStep =
		values.path === '0' && values.step === (presetSteps.length + 1).toString();

	useEffect(() => {
		if (
			autocompleteRef.current &&
			isSuccess &&
			!required &&
			(isFinalPresetStep || isFinalPromptStep)
		) {
			onGenerate(isFinalPromptStep);
		}
	}, [
		isSuccess,
		required,
		isFinalPresetStep,
		isFinalPromptStep,
		onGenerate,
		pathname,
		router,
		searchParams,
		values,
		isDemoUser,
	]);

	useEffect(() => {
		if (!confirmed && (isFinalPresetStep || isFinalPromptStep)) {
			void trigger({
				userId,
				location: window.location.toString() + '&autocomplete=true',
				force: true,
			});
		}
	}, [isFinalPresetStep, isFinalPromptStep, confirmed, trigger, userId]);

	const onSurpriseMe = async () => {
		if (
			isValueSimple(fields.name, values, 'name') &&
			values.name &&
			isValueSimple(fields.gender, values, 'gender')
		) {
			setPersonalityError('');
			try {
				await triggerGeneratePrompt({
					name: values.name || '',
					gender: values.gender || '',
					userId,
				}).unwrap();
			} catch (e) {
				Toaster.showError({
					titleKey: 'generalErrorTitle',
					textKey: 'generalErrorMessage',
				});
			}
		} else {
			setNameError('Enter character name to generate prompt');
		}
	};
	useUpdateQuery({ values, fields, customValues });

	useEffect(() => {
		document.getElementsByTagName('main')[0]?.scrollTo(0, 0);
	}, [values.step]);

	useEffect(() => {
		sendBotOpenedAnnals();
	}, [sendBotOpenedAnnals]);

	if (!values.step) {
		return null;
	}

	return (
		// navbar-hidden used to hide mobile NavigationBar on this page
		<StyledBotWizard className={cx(fadeIn, 'navbar-hidden')}>
			{!showEmailVer && (
				<div id={IdHideEmailConfirmModal} style={{ display: 'none' }} />
			)}
			<HeaderWrapper>
				<StyledTitle data-name="title" size="L">
					{t('title')}
					{values.step !== '0' && (
						<BackButton
							disabled={isGeneratedPromptLoading}
							href={updateQueryParamsWithPathname(
								{
									step: values.step
										? parseInt((values.step as BotWizardValueSimple)!) - 1
										: 0,
								},
								pathname,
								searchParams
							)}
							bigIcon={true}
							leftIcon={<ArrowBackIcon />}
							size="L"
						/>
					)}
				</StyledTitle>
				{values.step === '0' && (
					<Description data-name="subtitle" size="XS">
						{t('subtitle')}
					</Description>
				)}
				<HeaderStepsWrapper>
					{values.step !== '0' &&
						Array.from({
							length: values.path === '0' ? presetSteps.length + 3 : 3,
						}).map((_, index) => (
							<ProgressItem
								disabled={isGeneratedPromptLoading}
								values={values}
								key={index}
								fields={fields}
								index={index}
							/>
						))}
				</HeaderStepsWrapper>
			</HeaderWrapper>
			<StepWrapper className={fadeIn} key={values.step as string}>
				{values.step === '0' && (
					<>
						{firstStep.data.map((key) => (
							<Field
								id={key}
								values={values}
								customValues={customValues}
								key={key}
								field={fields[key]}
							/>
						))}
						<ChooseSteps values={values} />
					</>
				)}
				{values.step !== '0' &&
					values.path === '0' &&
					parseInt(values.step as string) < presetSteps.length + 1 &&
					(getStep() || [])
						.filter((key) => fields[key])
						.map((key) => (
							<Field
								nameError={nameError}
								customValues={customValues}
								id={key}
								values={values}
								key={key}
								field={fields[key]}
							/>
						))}
				{values.path === '0' &&
					parseInt(values.step as string) === presetSteps.length + 1 && (
						<PreviewStep
							previewPresetStep={previewPresetStep}
							customValues={customValues}
							values={values}
							fields={fields}
						/>
					)}
				{values.path === '1' && values.step === '1' && (
					<PromptStep
						personalityError={personalityError}
						isGeneratedPromptLoading={isGeneratedPromptLoading}
						nameError={nameError}
						personality={personality}
						setPersonality={(e) => {
							setPersonalityError('');
							setPersonality(e);
						}}
						look={look}
						setLook={(e) => {
							setLook(e);
						}}
						customValues={customValues}
						values={values}
						fields={fields}
						onSurpriseMe={onSurpriseMe}
					/>
				)}
			</StepWrapper>
			{!isGeneratedPromptLoading && (
				<NextButton
					isFinalPresetStep={isFinalPresetStep}
					isFinalPromptStep={isFinalPromptStep}
					onGenerate={onGenerate}
					setNameError={() => setNameError(t('requiredFieldErrorMessage'))}
					fields={fields}
					presetSteps={presetSteps}
					firstStep={firstStep}
					values={values}
					customValues={customValues}
				/>
			)}
			<LoadingModal
				isMale={values.gender === 'male'}
				name={
					isValueSimple(fields.name, values, 'name') ? values.name || '' : ''
				}
				quotes={
					t.raw('quotes') as Translated<
						BotWizardQuote & { image: { asset: { url: string } } }
					>[]
				}
				loaderTexts={t.raw('loaderTexts') as Translated<BotWizardLoaderText>[]}
				open={generationBotPromptLoading || generationBotPresetLoading}
			/>
			<ResultModal
				isPromptPath={values.path === '1'}
				open={!!generatedBotPrompt || !!generatedBotPreset}
				bot={generatedBotPrompt || generatedBotPreset}
				close={() => {
					resetGeneratedBotPrompt();
					resetGeneratedBotPreset();
				}}
			/>
			<ErrorModal
				errorCode={errorCode}
				open={isErrorModalOpen}
				onClose={errorModalClose}
				onAction={() => onGenerate(values.path === '1')}
			/>
		</StyledBotWizard>
	);
};
